import React from "react"
import { Link } from "react-router-dom"
import SEO from "../../components/SEO.jsx"

export default function LandingTemplate({ page }) {
  if (!page) {
    return (
      <div className="min-h-screen bg-white text-gray-900 px-6 pt-32">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-blue-900 mb-2">Landing page not found</h1>
          <p className="text-sm text-gray-700 mb-6">
            This URL does not match any path in landingPages.js.
          </p>
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition"
          >
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  const CTA_PRIMARY = page?.ctaPrimary || "Book a Free Website Health Check"
  const CTA_SECONDARY = page?.ctaSecondary || "Start Your Website Project →"
  const CTA_TO = page?.ctaTo || "/contact"

  return (
    <div className="bg-white text-gray-900">
      <SEO
        title={page.title || "2merative Web Design"}
        description={page.description || ""}
        path={page.path || "/"}
        image={page.image || "/og-image.png"}
      />

      <main className="pt-28 sm:pt-32 pb-20 px-4">
        {/* HERO */}
        <section className="max-w-5xl mx-auto text-center mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-900 mb-4">
            {page.heroHeading || "Landing Page"}
          </h1>
          <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
            {page.heroSubheading || ""}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to={CTA_TO}
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition"
            >
              {CTA_PRIMARY}
            </Link>
            <Link to={CTA_TO} className="inline-block text-blue-700 font-medium hover:underline">
              {CTA_SECONDARY}
            </Link>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            {page.heroNote || "No pushy sales. A simple chat about what you need and what fits your budget."}
          </p>
        </section>

        {/* PROBLEM */}
        <section className="max-w-5xl mx-auto mb-16">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-2xl font-bold text-blue-900 mb-4">
                {page.problemHeading || "What’s getting in the way"}
              </h2>

              {(page.problemParagraphs || []).map((p, idx) => (
                <p key={idx} className="text-gray-700 mb-3">
                  {p}
                </p>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                {page.problemBoxHeading || "Common signs"}
              </h3>
              <ul className="space-y-2 text-gray-800 text-sm">
                {(page.problemBullets || []).map((b, idx) => (
                  <li key={idx}>• {b}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* OFFER */}
        <section className="max-w-5xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">
            {page.offerHeading || "What you get"}
          </h2>
          <p className="text-center text-gray-700 mb-8 max-w-3xl mx-auto">
            {page.offerLead || ""}
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {(page.offerCards || []).slice(0, 3).map((c, idx) => (
              <div key={idx} className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">{c.title}</h3>
                <p className="text-sm text-gray-700">{c.text}</p>
              </div>
            ))}
          </div>

          {page.offerCards?.length > 3 && (
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              {page.offerCards.slice(3, 6).map((c, idx) => (
                <div key={idx} className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{c.title}</h3>
                  <p className="text-sm text-gray-700">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* STEPS */}
        <section className="max-w-5xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">
            {page.stepsHeading || "How it works"}
          </h2>

          <div className="grid md:grid-cols-4 gap-4 text-sm">
            {(page.steps || []).slice(0, 4).map((s, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                <p className="text-xs font-semibold text-blue-700 mb-1">Step {idx + 1}</p>
                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-700">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="max-w-4xl mx-auto text-center bg-blue-50 border border-blue-100 rounded-3xl p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 mb-4">
            {page.finalHeading || "Ready to take the next step?"}
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            {page.finalLead || ""}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to={CTA_TO}
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition"
            >
              {CTA_PRIMARY}
            </Link>
            <Link to={CTA_TO} className="inline-block text-blue-700 font-medium hover:underline">
              {CTA_SECONDARY}
            </Link>
          </div>

          <p className="mt-4 text-sm text-gray-700">
            You can also explore our <Link to="/website-landing" className="text-blue-700 hover:underline">website packages</Link>, learn more about <Link to="/web-design-adelaide" className="text-blue-700 hover:underline">web design in Adelaide</Link>, or <Link to="/contact" className="text-blue-700 hover:underline">contact 2merative</Link>.
          </p>
        </section>
      </main>
    </div>
  )
}
